/**
 * Shared per-frame boat positions for live explore replay map.
 * Computed once in LiveMapContainer; consumed by boat + track layers.
 */

import {
  assignInterpolatedHeading,
  motionHeadingDeg,
} from "./trackInterpolation";
import { defaultChannelsStore } from "../store/defaultChannelsStore";

function readFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

export function livePointTimestamp(d: unknown): number {
  if (!d || typeof d !== "object") return 0;
  const row = d as Record<string, unknown>;
  const ts =
    row.timestamp ??
    (row.Datetime instanceof Date
      ? row.Datetime.getTime()
      : row.Datetime != null
        ? new Date(String(row.Datetime)).getTime()
        : 0);
  return Number.isFinite(Number(ts)) ? Number(ts) : 0;
}

export function livePointCoords(d: unknown): { lat: number; lng: number } | null {
  if (!d || typeof d !== "object") return null;
  const row = d as Record<string, unknown>;
  const latName = defaultChannelsStore.latName() || "Lat_dd";
  const lngName = defaultChannelsStore.lngName() || "Lng_dd";
  const latRaw = row[latName] ?? row.lat_dd ?? row.Lat_dd ?? row.Lat ?? row.lat;
  const lngRaw = row[lngName] ?? row.lng_dd ?? row.Lng_dd ?? row.Lng ?? row.lng;
  const lat = readFiniteNumber(latRaw);
  const lng = readFiniteNumber(lngRaw);
  if (lat === undefined || lng === undefined) return null;
  return { lat, lng };
}

export function livePointHdg(d: unknown): number | undefined {
  if (!d || typeof d !== "object") return undefined;
  const row = d as Record<string, unknown>;
  const hdgName = defaultChannelsStore.hdgName() || "Hdg_deg";
  const raw = row[hdgName] ?? row.hdg_deg ?? row.Hdg_deg ?? row.Hdg ?? row.hdg;
  return readFiniteNumber(raw);
}

export function livePointCog(d: unknown): number | undefined {
  if (!d || typeof d !== "object") return undefined;
  const row = d as Record<string, unknown>;
  let cogName = "Cog_deg";
  try {
    if (typeof defaultChannelsStore.isReady === "function" && defaultChannelsStore.isReady()) {
      cogName = defaultChannelsStore.cogName() || cogName;
    }
  } catch {
    /* store not ready */
  }
  const raw = row[cogName] ?? row.cog_deg ?? row.Cog_deg ?? row.Cog ?? row.cog;
  return readFiniteNumber(raw);
}

/**
 * Icon heading for live replay: sensor Hdg from the real sample,
 * then COG, then track tangent only when neither is available.
 */
export function resolveLiveReplayDisplayHeading(
  point: unknown,
  prevPoint: unknown | null | undefined
): number | undefined {
  const sensorHdg = livePointHdg(point);
  if (sensorHdg != null) return sensorHdg;

  const cog = livePointCog(point);
  if (cog != null) return cog;

  const coords = livePointCoords(point);
  const prevCoords = prevPoint ? livePointCoords(prevPoint) : null;
  if (coords && prevCoords) {
    return motionHeadingDeg(prevCoords.lat, prevCoords.lng, coords.lat, coords.lng);
  }
  return undefined;
}

function enrichLiveReplayFramePoint(point: unknown, prevPoint: unknown | null | undefined): unknown {
  const displayHdg = resolveLiveReplayDisplayHeading(point, prevPoint);
  if (displayHdg == null || typeof point !== "object" || !point) return point;
  const enriched = { ...(point as object) };
  assignInterpolatedHeading(enriched as Record<string, unknown>, displayHdg);
  return enriched;
}

/** Instantaneous boat speed (kph) from live sample fields — never normalized Bsp/bsp. */
export function livePointBspKph(d: unknown): number | undefined {
  if (!d || typeof d !== "object") return undefined;
  const row = d as Record<string, unknown>;
  let bspName = "Bsp_kph";
  try {
    if (typeof defaultChannelsStore.isReady === "function" && defaultChannelsStore.isReady()) {
      bspName = defaultChannelsStore.bspName() || bspName;
    }
  } catch {
    /* store not ready */
  }
  const candidates = [bspName, "Bsp_kph", "boat_speed_km_h_1", "Bsp_kts"].filter(Boolean);
  for (const field of candidates) {
    const raw = row[field as string];
    const n = readFiniteNumber(raw);
    if (n === undefined) continue;
    const abs = Math.abs(n);
    if (abs <= 0) continue;
    if (String(field).endsWith("_kts")) return abs * 1.852;
    return abs;
  }
  return undefined;
}

function latestRealPointAtOrBefore(
  catalog: unknown[],
  playheadMs: number
): { point: unknown; prevPoint: unknown | null } | null {
  let latestIndex = -1;
  for (let i = catalog.length - 1; i >= 0; i--) {
    const ts = livePointTimestamp(catalog[i]);
    if (ts > 0 && ts <= playheadMs && livePointCoords(catalog[i]) !== null) {
      latestIndex = i;
      break;
    }
  }

  if (latestIndex < 0) return null;

  let prevPoint: unknown | null = null;
  for (let i = latestIndex - 1; i >= 0; i--) {
    const ts = livePointTimestamp(catalog[i]);
    if (ts > 0 && livePointCoords(catalog[i]) !== null) {
      prevPoint = catalog[i];
      break;
    }
  }

  return { point: catalog[latestIndex], prevPoint };
}

/** One real sample per selected source: latest point at or before playhead. */
export function buildLiveReplayFramePoints(
  selected: Set<number>,
  catalogBySource: Map<number, unknown[]>,
  playhead: Date | null | undefined
): Map<number, unknown> {
  const result = new Map<number, unknown>();
  if (!(playhead instanceof Date) || selected.size === 0) return result;

  const playheadMs = playhead.getTime();
  for (const sourceId of selected) {
    const catalog = catalogBySource.get(sourceId);
    if (!catalog || catalog.length === 0) continue;
    const latest = latestRealPointAtOrBefore(catalog, playheadMs);
    if (!latest) continue;
    result.set(sourceId, enrichLiveReplayFramePoint(latest.point, latest.prevPoint));
  }
  return result;
}

export type LiveReplaySourceStyle = { sourceName: string; color: string };

/** Memo-friendly sourceId → display name + color. */
export function buildLiveReplaySourceStyles(
  sources: Array<{ source_id?: number | string; source_name?: string; color?: string }>,
  getSourceColor: (name: string) => string | undefined
): Map<number, LiveReplaySourceStyle> {
  const map = new Map<number, LiveReplaySourceStyle>();
  for (const s of sources) {
    const id = Number(s.source_id);
    if (!Number.isFinite(id)) continue;
    const sourceName = s.source_name || `Source ${id}`;
    map.set(id, {
      sourceName,
      color: s.color || getSourceColor(sourceName) || "#1f77b4",
    });
  }
  return map;
}
